import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import Breadcrumbs from '../../../../components/Breadcrumbs/Breadcrumbs';
import UserDetailsSimsTable from '../components/UserDetailsSimsTable';
import AssignSimDrawer from '../components/AssignSimDrawer/AssignSimDrawer';

import UserDetailsContext from '../../../../contexts/UserDetailsContext/UserDetailsContext';

import { GetDeviceFn } from '../../../../models/UserDevicesModels';

import useUserDetails from '../useUserDetails';
import DeviceDetailsInfoRows from './DeviceDetailsInfoRows';
import useDeviceDetails from './useDeviceDetails';

interface DevicesReportProps {
  unassignedDevice?: boolean;
}

function DeviceDetails({ unassignedDevice = false }: DevicesReportProps) {
  const [t] = useTranslation();
  const { id, deviceId } = useParams();
  const { pathname } = useLocation();
  const { user } = useUserDetails(id);
  const [showAssignSim, setShowAssignSim] = useState(false);
  const {
    device,
    sims,
    fetchDeviceDetails,
    updateDeviceDetails,
    resendNotification,
    isLoading,
    updateSimDetails,
    deleteDeviceHandler,
    cancelScheduledOperation,
  } = useDeviceDetails(deviceId, id, unassignedDevice, pathname);

  /**
   * Returns the current device
   */
  const getDevice: GetDeviceFn = () => device;

  /**
   * Init assign sim
   */
  const assignSim = () => {
    setShowAssignSim(true);
  };

  return (
    <UserDetailsContext.Provider value={user}>
      <Breadcrumbs
        activeLinks={[
          ...(pathname.startsWith('/dashboard/devicesReport')
            ? [
                { label: t('navbar.dashboard'), url: '/dashboard' },
                { label: t('pages.devicesReport.title'), url: '/dashboard/devicesReport' },
              ]
            : [{ label: t('navbar.esimManager'), url: '/esimManager' }]),
          ...(!unassignedDevice
            ? [
                {
                  label: user ? `${user?.firstName} ${user?.lastName}` : '',
                  url: !pathname.startsWith('/dashboard/devicesReport')
                    ? `/esimManager/userDetails/${id}`
                    : `/dashboard/devicesReport/userDetails/${id}`,
                },
              ]
            : []),
        ]}
        disabledLinks={[
          ...(unassignedDevice ? [t('pages.userDetails.unassigned')] : []),
          device ? `${device?.manufacturer} ${device?.model}` : '',
        ]}
      />
      <DeviceDetailsInfoRows
        device={device}
        sims={sims}
        isLoading={isLoading}
        updateDeviceDetails={updateDeviceDetails}
        deleteDevice={deleteDeviceHandler}
        assignSim={assignSim}
      />
      <UserDetailsSimsTable
        sims={sims}
        isLoading={isLoading}
        showTotalSims={false}
        isPerformingAction={isLoading}
        resendNotification={resendNotification}
        getDeviceById={getDevice}
        onNewConnectionSuccess={fetchDeviceDetails}
        onUpgradeSuccess={fetchDeviceDetails}
        onSuspendSimSuccess={fetchDeviceDetails}
        onRestoreConnectionSuccess={fetchDeviceDetails}
        onTerminateConnectionSuccess={fetchDeviceDetails}
        updateSim={updateSimDetails}
        cancelScheduledOperation={cancelScheduledOperation}
        assignSim={assignSim}
      />
      {showAssignSim ? (
        <AssignSimDrawer
          onClose={() => {
            setShowAssignSim(false);
          }}
          open={showAssignSim}
          onAssignSimSuccess={fetchDeviceDetails}
          device={device}
        />
      ) : null}
    </UserDetailsContext.Provider>
  );
}

export default DeviceDetails;
